import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import * as api from "../services/api";

const planBadge: Record<string, string> = {
  free: "bg-gray-100 text-gray-600",
  pro: "bg-indigo-100 text-indigo-700",
  enterprise: "bg-purple-100 text-purple-700",
};

export default function Settings() {
  const { user, setUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Profile section
  const [name, setName] = useState(user?.name ?? "");
  const [savingName, setSavingName] = useState(false);
  const [nameMsg, setNameMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Password section
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ ok: boolean; text: string } | null>(null);

  if (authLoading) return null;
  if (!user) {
    navigate("/login");
    return null;
  }

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingName(true);
    setNameMsg(null);
    try {
      const res = await api.updateMe({ name });
      setUser(res.user);
      setNameMsg({ ok: true, text: "Display name updated." });
    } catch (err: any) {
      setNameMsg({ ok: false, text: err?.message || "Failed to update name." });
    } finally {
      setSavingName(false);
    }
  };

  const handleSavePw = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg(null);
    if (newPw !== confirmPw) {
      setPwMsg({ ok: false, text: "New passwords do not match." });
      return;
    }
    if (newPw.length < 8) {
      setPwMsg({ ok: false, text: "Password must be at least 8 characters." });
      return;
    }
    setSavingPw(true);
    try {
      await api.updateMe({ currentPassword: currentPw, newPassword: newPw });
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      setPwMsg({ ok: true, text: "Password changed successfully." });
    } catch (err: any) {
      setPwMsg({ ok: false, text: err?.message || "Failed to change password." });
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
          <p className="text-indigo-600 font-semibold uppercase tracking-widest text-sm mb-1">Account</p>
          <h1 className="text-3xl font-extrabold text-gray-900">Settings</h1>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left: account summary */}
          <div className="lg:col-span-1 space-y-5">
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold select-none">
                  {(user.name?.[0] ?? user.email?.[0] ?? "?").toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{user.name}</p>
                  <p className="text-sm text-gray-500">{user.email}</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Plan</span>
                <span className={`px-2.5 py-0.5 rounded-full font-semibold capitalize text-xs ${planBadge[user.plan] ?? planBadge.free}`}>
                  {user.plan ?? "free"}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-gray-500">Role</span>
                <span className="text-gray-700 font-medium capitalize">{user.role ?? "user"}</span>
              </div>
              {(user.plan === "free") && (
                <a
                  href="/pricing"
                  className="mt-5 block text-center px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold shadow hover:opacity-90 transition"
                >
                  Upgrade to Pro →
                </a>
              )}
            </div>
          </div>

          {/* Right: edit forms */}
          <div className="lg:col-span-2 space-y-6">

            {/* Profile name */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Profile</h2>
              <form onSubmit={handleSaveName} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
                  <input
                    type="email"
                    value={user.email}
                    disabled
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-gray-500 text-sm cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-400 mt-1">Email cannot be changed.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={80}
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm"
                  />
                </div>
                {nameMsg && (
                  <p className={`text-sm font-medium ${nameMsg.ok ? "text-green-600" : "text-red-600"}`}>{nameMsg.text}</p>
                )}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={savingName || name === user.name}
                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold shadow hover:opacity-90 transition disabled:opacity-40"
                  >
                    {savingName ? "Saving…" : "Save name"}
                  </button>
                </div>
              </form>
            </div>

            {/* Change password */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Change Password</h2>
              <form onSubmit={handleSavePw} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current password</label>
                  <input
                    type="password"
                    value={currentPw}
                    onChange={(e) => setCurrentPw(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
                    <input
                      type="password"
                      value={newPw}
                      onChange={(e) => setNewPw(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</label>
                    <input
                      type="password"
                      value={confirmPw}
                      onChange={(e) => setConfirmPw(e.target.value)}
                      required
                      autoComplete="new-password"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm"
                    />
                  </div>
                </div>
                {pwMsg && (
                  <p className={`text-sm font-medium ${pwMsg.ok ? "text-green-600" : "text-red-600"}`}>{pwMsg.text}</p>
                )}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={savingPw || !currentPw || !newPw || !confirmPw}
                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold shadow hover:opacity-90 transition disabled:opacity-40"
                  >
                    {savingPw ? "Changing…" : "Change password"}
                  </button>
                </div>
              </form>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
